import { useEffect } from "react"
import { makeStyles, Paper, CircularProgress } from "@material-ui/core";
import { Trans, useTranslation } from "react-i18next";
import { AlertStatusCard } from "../../../../yoello-lib/modules/components/UI/Alerts/AlertStatusCard";
import DynamicForm from "../../../../yoello-lib/modules/dynamicForms/DynamicForms";
import FormSwitchWithText from "../../../../yoello-lib/modules/dynamicForms/FormComponents/FormSwitchWithText";
import EColourPanel from "../../../../yoello-lib/modules/dynamicForms/interfaces/EColourPanel";
import { OBformEntity, splitSortCode } from "./OBFormEntity";
import { ReactComponent as InfoCircleIcon } from "../../../../assets/images/icon-info-circle.svg"
import { useDispatch, useSelector } from "react-redux";
import { setDialogHandle } from "../../../../actions/dialog";
import Styles from "../../../../scss/openbanking.module.scss";
import { ReactComponent as EditIcon } from "../../../../assets/images/edit-icon.svg";
import { ConfirmDialog } from "./ConfirmDialog";
import { useState } from "react";
import { useOpenBanking } from "../../../../hooks/useOpenBanking";
import IDynamicFormRow from "../../../../yoello-lib/modules/dynamicForms/interfaces/IDynamicFormRow";
import { IReduxAppState } from "../../../../reducers";

const useStyles = makeStyles({
    root: {
        padding: "2rem",
    },
    formTitle:{
        "&>p:first-child":{
            fontFamily: 'Open Sans',
            fontStyle: "normal",
            fontWeight: 600,
            fontSize: "1rem",
            lineHeight: "1.25rem",
            color: "#212121", 
            margin:0,
            marginBottom:"1rem"
        },
        "&>p:last-child":{
            fontFamily: 'Open Sans',
            fontStyle: "normal",
            fontWeight: 400,
            fontSize: "0.9375rem",
            lineHeight: "1.625rem",
            color: "#737FA4",
            margin:0,
            marginBottom:"1.875rem"
        }
    },
    infoBox: {
        margin: "1rem 0"
    },
    dialogTitle: {
        margin: "1.5rem",
        textAlign: "center"
    },

})


export function OpenBankingForm() {
    const { t } = useTranslation();
    const { root, infoBox, dialogTitle , formTitle } = useStyles();
    const [formRows, setFormRows] = useState<IDynamicFormRow[] | undefined>(undefined);
    const dispatch = useDispatch();
    const [formData, setFormData] = useState({ enabled: false });
    const [isDataChanged, setIsDataChanged] = useState(false);
    const { handleAccount, account, accountLoading } = useOpenBanking();
    const paymentStage = useSelector((state: IReduxAppState) => state.payments.paymentStage);
    const currentLanguage = useSelector((state: IReduxAppState) => state.language.current);

    useEffect(() => {
        if (account) {
            //fixing sort_code's length issue on editing account details by merchant  
            const maskedAccount = {
                ...account,
                sort_code: splitSortCode(account.sort_code!)
            }
            setFormData({
                ...maskedAccount,
                enabled: maskedAccount.enabled ?? formData.enabled
            });

        }
        // eslint-disable-next-line   
    }, [account]);

    useEffect(() => {
        const rows: IDynamicFormRow[] = [
            {
                label: "Open Banking Enabled?",
                keyName: "enabled",
                Component: FormSwitchWithText,
                formProps: { secondLabel: t("openBanking.form.enabledSecongLabel"), hasStatus: true },
                disabled: paymentStage !== 4
            },
            {
                label: "",
                keyName: "obdata",
                Component: OBformEntity,
            }
        ]
        setFormRows(rows)
        // eslint-disable-next-line  
    }, [account, paymentStage, currentLanguage]);

    function onSubmit(data: any) {
        setFormData(data);
        dispatch(setDialogHandle({
            Component: ConfirmDialog,
            ComponentProps: { data, handleAccount },
            isFormDialog: false,
            styles: {
                className: `${Styles.OpenBankingModule}`
            },
            dialogTitle: <div className={Styles.ActionMessages}>
                <EditIcon />
                <p className={dialogTitle}>
                    {t("openBanking.form.dialogTitle")}
                </p>
            </div>
        }))
    }
    const handleDataChanged = (isChanged: boolean) => {
        setIsDataChanged(isChanged)
    }
    return <Paper className={`${root} dialog`}>
        {accountLoading ? <div style={{ margin: "6rem 10rem", display: "flex", alignItems: "center", justifyContent: "center" }}><CircularProgress /></div> :
            <>
                <div className={formTitle}>
                    <p>{t('openBanking.form.title')}</p>
                    <p>{t('openBanking.form.description')}</p>
                </div>
                <DynamicForm
                    onHandleSubmit={onSubmit}
                    hasDataChanged={handleDataChanged}
                    formData={formData}
                    formRows={formRows!}
                    formButtons={{
                        rightButton: {
                            type: "submit",
                            label: t("Update Open Banking"),
                            color: EColourPanel.PRIMARY,
                            disabled: (paymentStage !== 4 || !isDataChanged)
                        },
                    }}
                />

            </>
        }

        <AlertStatusCard alertProps={{ icon: <InfoCircleIcon />, severity: 'info', className: infoBox }}
            alertMessage={<Trans i18nKey="openBanking.form.infoBox"
                components={{ bold: <strong /> }} />}
        />
    </Paper>

}